import { useMemo, useState } from 'react';
import { BiSearch } from 'react-icons/bi';

import { Avatar } from '@/components/Avatar/Avatar';
import { Input } from '@/components/Inputs';
import Modal from '@/components/Modal/Modal';
import type { MappedPersonForSelection } from '@/store/persons/person.dto';

import type { CrewRosterEntry } from '../types';

import s from './UserPickerModal.module.scss';

interface UserPickerModalProps {
  open: boolean;
  users: MappedPersonForSelection[];
  disabledIds: Set<string>;
  onSelect?: (user: CrewRosterEntry) => void;
  onSelectUser?: (user: MappedPersonForSelection) => void;
  onClose: () => void;
  title?: string;
}

export function UserPickerModal({
  open,
  users,
  disabledIds,
  onSelect,
  onSelectUser,
  onClose,
  title = 'Add Crew Member',
}: UserPickerModalProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      users.filter((u) => {
        const name = `${u.firstName} ${u.lastName}`.toLowerCase();
        const role = (u.role || '').toLowerCase();
        const query = search.toLowerCase();
        return name.includes(query) || role.includes(query);
      }),
    [users, search],
  );

  const handleSelect = (user: MappedPersonForSelection) => {
    if (onSelectUser) {
      onSelectUser(user);
    } else if (onSelect) {
      const crewEntry: CrewRosterEntry = {
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        avatar: user.avatar,
        roleName: user.role || '',
        status: 'on_board',
        dayOff: false,
        hol: false,
      };
      onSelect(crewEntry);
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className={s.container}>
        <Input
          autoFocus
          placeholder="Alex Morgan"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startIcon={<BiSearch size={20} />}
        />
        <div className={s.list}>
          {filtered.length === 0 ? (
            <p className={s.empty}>No users match. Please try another search.</p>
          ) : (
            filtered.map((user) => {
              const disabled = disabledIds.has(user.id);
              return (
                <button
                  type="button"
                  key={user.id}
                  className={s.row}
                  onClick={() => handleSelect(user)}
                  disabled={disabled}
                >
                  <Avatar
                    firstName={user.firstName}
                    lastName={user.lastName}
                    src={user.avatar}
                    size={40}
                  />
                  <div className={s.info}>
                    <div className={s.name}>
                      {user.firstName} {user.lastName}
                    </div>
                    {user.role && <div className={s.role}>{user.role}</div>}
                  </div>
                  {disabled && <div className={s.badge}>Added</div>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}
